/**
 * A cubic chunk of voxels.
 *
 * 16x16x16 matches Minecraft's section size and is a good tradeoff:
 * small enough that a single chunk rebuild is cheap, large enough that we
 * aren't drowning in draw calls.
 */

export const CHUNK_SIZE = 16;
export const CHUNK_AREA = CHUNK_SIZE * CHUNK_SIZE;
export const CHUNK_VOLUME = CHUNK_AREA * CHUNK_SIZE;

/**
 * Index order is (y, z, x) - x varies fastest.
 * The mesher scans x innermost, so this keeps its reads sequential in memory.
 */
export function blockIndex(x: number, y: number, z: number): number {
  return (y * CHUNK_SIZE + z) * CHUNK_SIZE + x;
}

/**
 * One block of margin on every side.
 *
 * The mesher has to see a chunk's neighbours to know which faces are hidden.
 * Copying a padded cube once is what lets meshing run in a worker at all - the
 * worker gets a self-contained volume instead of needing the whole world.
 */
export const PAD = 1;
export const PADDED_SIZE = CHUNK_SIZE + 2 * PAD;
export const PADDED_VOLUME = PADDED_SIZE * PADDED_SIZE * PADDED_SIZE;

/** Index into a padded volume. Coordinates run -1..CHUNK_SIZE. */
export function paddedIndex(x: number, y: number, z: number): number {
  return ((y + PAD) * PADDED_SIZE + (z + PAD)) * PADDED_SIZE + (x + PAD);
}

export class Chunk {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;

  /** Flat voxel storage. Uint16 leaves room for 65k block types. */
  readonly blocks: Uint16Array;

  /**
   * Light, packed sky in the high nibble and torch in the low, one byte a
   * cell - the same shape Minecraft keeps per chunk section, and for the same
   * reason.
   *
   * **It used to be one array over the whole world**, and that is what capped
   * how big a world could be: two flat maps covering every cell, twenty-five
   * megabytes for a three-hundred-block map and impossible for an endless
   * one. Here it costs four kilobytes per chunk that exists and nothing at
   * all for the sky, because a chunk of pure air is never stored.
   *
   * Null until the chunk has been lit. Absent means "not worked out yet",
   * which the world reads as daylight above ground - see `World.lightPacked`.
   */
  light: Uint8Array | null = null;

  /** Non-air block count. When 0 the chunk needs no mesh at all. */
  private nonAir = 0;

  /**
   * Bumped on every change.
   *
   * Meshing is asynchronous, so a result can come back for a chunk that has
   * already changed again. Comparing versions is how the renderer tells a
   * fresh mesh from a stale one - a boolean dirty flag cannot express that.
   */
  version = 0;

  constructor(cx: number, cy: number, cz: number) {
    this.cx = cx;
    this.cy = cy;
    this.cz = cz;
    this.blocks = new Uint16Array(CHUNK_VOLUME);
  }

  /** True when the chunk is entirely air - skip meshing and rendering. */
  get isEmpty(): boolean {
    return this.nonAir === 0;
  }

  /** Local coordinates, 0..15. No bounds check - callers must be in range. */
  get(x: number, y: number, z: number): number {
    return this.blocks[blockIndex(x, y, z)]!;
  }

  set(x: number, y: number, z: number, id: number): void {
    const i = blockIndex(x, y, z);
    const prev = this.blocks[i]!;
    if (prev === id) return;
    if (prev === 0 && id !== 0) this.nonAir++;
    else if (prev !== 0 && id === 0) this.nonAir--;
    this.blocks[i] = id;
    this.version++;
  }

  /** Mark the chunk as changed without editing it - used by neighbours. */
  touch(): void {
    this.version++;
  }

  /** Recount after a bulk write that bypassed `set` (e.g. world generation). */
  recount(): void {
    let n = 0;
    const b = this.blocks;
    for (let i = 0; i < b.length; i++) if (b[i] !== 0) n++;
    this.nonAir = n;
  }
}
